import React, { useContext, useEffect, useState } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { Button } from '@rneui/themed';
import { addDays, addWeeks, subDays, subWeeks, format, parseISO, isBefore, isAfter } from 'date-fns';
import PropTypes from 'prop-types';
import Calendar from './Calendar/Calendar';
import { Colors } from './Colors';
import { Toolbar } from './Toolbar';
import { H1, H5 } from '../../text';
import { fetchPage } from '../../../common/Api';
import Utils from '../../../common/Utils';
import AppContext from '../../../contexts/App';
import LayoutContext from '../../../contexts/Layout';
import { EventGridItem } from '../EventGrid/EventGridItem';
import { Grid } from '../../common/Grid';

import FilterMenu from './FilterMenu';
import DatePickerMenu from './DatePickerMenu';

const EventCalendar = ({ events, nodeTypeAlias = 'EventsCalendar', subTitle, title, width, hardWidth }) => {
    const eventItemsInitialCount = 50;
    const eventItemsLoadMoreCount = 20;
    const { dispatch } = useContext(AppContext);
    const { layout } = useContext(LayoutContext);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [filters, setFilters] = useState();
    const displayedEvents = [];
    const [calendarLayoutStyle, setCalendarLayoutStyle] = useState(Object.values(CalendarLayoutStyle).indexOf(CalendarLayoutStyle.eventBased));
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [eventItemsMax, setEventItemsMax] = useState(eventItemsInitialCount);

    const [filterMenuVisible, setFilterMenuVisible] = useState(false);
    const [datePickerVisible, setDatePickerVisible] = useState(false);

    useEffect(() => {
        console.log('eventItemsMax change', eventItemsMax);
    }, [eventItemsMax]);

    const toggleCalendarLayout = () => {
        let id = calendarLayoutStyle + 1;
        if (id >= Object.keys(CalendarLayoutStyle).length) id = 0;
        setCalendarLayoutStyle(id);
    };

    const handleNavigation = e => {
        if (!selectedDate) {
            return;
        }

        if (e.equals('TODAY')) {
            setSelectedDate(new Date());
        } else if (e.equals('NEXT')) {
            setSelectedDate(layout.device.isDesktopOrLaptop ? addWeeks(selectedDate, 1) : addDays(selectedDate, 1));
        } else if (e.equals('PREV')) {
            setSelectedDate(layout.device.isDesktopOrLaptop ? subWeeks(selectedDate, 1) : subDays(selectedDate, 1));
        }
    };

    useEffect(() => {
        const targetDate = new Date();
        targetDate.setDate(targetDate.getDate() - 1);

        const _events = [...events]
            .filter(o => new Date(o.startDateAndTime) >= targetDate || (o.isAllDayEvent && new Date(o.endDateAndTime) >= targetDate))
            .sort((a, b) => new Date(a.startDateAndTime) - new Date(b.startDateAndTime));

        _events.length = 104;

        setFilteredEvents(_events);
    }, [events]);

    useEffect(() => {
        const campusTags = [];
        const ministryTags = [];
        let colorCounter = 0;

        events.forEach(event => {
            const campuses = event.campus;

            if (campuses?.length > 0) {
                campuses.forEach(campus => {
                    if (campusTags.findIndex(tag => tag.label.equals(campus)) === -1) {
                        campusTags.push({ label: campus, isChecked: false, color: Colors[colorCounter] });
                        colorCounter++;
                    }
                });
            }

            const ministries = event.ministry;

            if (ministries?.length > 0) {
                ministries.forEach(ministry => {
                    if (ministryTags.findIndex(tag => tag.label.equals(ministry)) === -1) {
                        ministryTags.push({ label: ministry, isChecked: false, color: Colors[colorCounter] });
                        colorCounter++;
                    }
                });
            }
        });

        setFilters({
            all: { label: 'All', isChecked: true },
            campuses: campusTags,
            ministries: ministryTags
        });
    }, [events]);

    if (!events || !filters) {
        return null;
    }

    events.forEach(event => {
        const id = event.id;
        const start = event.startDateAndTime;
        const end = event.endDateAndTime;
        const eventTitle = event.eventTitle;
        const campuses = event.campus;
        const ministries = event.ministry;
        const isAllDay = event.isAllDayEvent;
        const rootPath = event.rootPath;
        const rootEvent = event;

        const startDate = typeof start === 'string' ? parseISO(start) : new Date(start);
        const endDate = typeof end === 'string' ? parseISO(end) : new Date(end);

        const calendarEvent = {
            backgroundColor: Colors[0],
            id,
            start: startDate,
            end: endDate,
            title: eventTitle,
            past: isBefore(startDate, new Date()),
            isAllDayEvent: isAllDay,
            rootPath,
            rootEvent
        };

        if (filters) {
            let addEvent = filters.all.isChecked;

            if (campuses) {
                const foundCampus = filters.campuses.find(campus => campuses.findIndex(c => campus.label.equals(c)) !== -1);

                if (foundCampus) {
                    calendarEvent.backgroundColor = foundCampus.color;
                    calendarEvent.color = foundCampus.color;

                    if (foundCampus.isChecked) {
                        addEvent = true;
                    }
                }
            }

            if (ministries) {
                const foundMinistry = filters.ministries.find(ministry => ministries.findIndex(m => ministry.label.equals(m)) !== -1);

                if (foundMinistry) {
                    calendarEvent.backgroundColor = foundMinistry.color;
                    calendarEvent.color = foundMinistry.color;

                    if (foundMinistry.isChecked) {
                        addEvent = true;
                    }
                }
            }

            if (addEvent) {
                displayedEvents.push(calendarEvent);
            }
        }
    });

    const calendarBased = props => {
        return (
            <>
                {!!title && (
                    <View style={{ flexDirection: 'row', marginVertical: layout.styleGuide.spacer1 }}>
                        <H1 text={title} />
                        {!!subTitle && (
                            <Text
                                style={{
                                    fontFamily: 'GothamBook',
                                    fontSize: layout.device.os.equals('web') ? '18pt' : 18,
                                    lineHeight: layout.device.os.equals('web') ? '20pt' : 20,
                                    letterSpacing: 0
                                }}
                            >
                                {subTitle}
                            </Text>
                        )}
                    </View>
                )}
                <View style={{ flexDirection: 'row' }}>
                    {layout.device.isDesktopOrLaptop && (
                        <Button
                            containerStyle={{ alignSelf: 'center' }}
                            icon={{
                                color: '#153d6e',
                                name: 'chevron-left-circle-outline',
                                size: 30,
                                type: 'material-community'
                            }}
                            onPress={() => handleNavigation('PREV')}
                            type='clear'
                        />
                    )}
                    <View style={{ flex: 1 }}>
                        <Toolbar
                            applyFilters={appliedFilters => {
                                setEventItemsMax(eventItemsInitialCount);
                                setFilters(appliedFilters);
                            }}
                            filters={filters}
                            handleDateChange={e => setSelectedDate(e)}
                            label={format(selectedDate, 'MMMM yyyy')}
                            onNavigate={e => handleNavigation(e)}
                            selectedDate={selectedDate}
                        />
                        <Calendar
                            ampm={true}
                            date={selectedDate}
                            endHour={22}
                            events={displayedEvents}
                            height={layout.dimensions.height}
                            mode={layout.device.isDesktopOrLaptop ? 'week' : 'day'}
                            onEventPress={async e => await fetchPage(dispatch, Utils.cleanupInternalUrl(e.rootPath))}
                            onNavigate={e => handleNavigation(e)}
                            onRefresh={() => {}}
                            showTime={true}
                            startHour={6}
                            swipeEnabled={true}
                            weekStartsOn={0}
                        />
                    </View>
                    {layout.device.isDesktopOrLaptop && (
                        <Button
                            containerStyle={{ alignSelf: 'center' }}
                            icon={{
                                color: '#153d6e',
                                name: 'chevron-right-circle-outline',
                                size: 30,
                                type: 'material-community'
                            }}
                            onPress={() => handleNavigation('NEXT')}
                            type='clear'
                        />
                    )}
                </View>
            </>
        );
    };

    const eventBased = () => {
        // const itemsMax = 50; eventItemsLoadMoreCount

        const unionEvents = displayedEvents.filter(item => filteredEvents.find(f => f?.id === item?.rootEvent?.id));
        const futureEvents = unionEvents.filter(item => isAfter(item.start, new Date()));
        const sortedEvents = futureEvents.sort((a, b) => a.start.getTime() - b.start.getTime());
        const gridItems = sortedEvents.map((item, index) => {
            const { key, enableImageOverlay, ...itemProps } = item.rootEvent;

            return <EventGridItem {...itemProps} enableImageOverlay={enableImageOverlay} index={index} width={hardWidth} />;
        });
        if (gridItems.length > eventItemsMax) gridItems.length = eventItemsMax;

        return (
            <View
                style={{ marginBottom: layout.styleGuide.componentSeparator }}
                dataSet={{ umbracoComponentName: nodeTypeAlias, spacerBottom: `StyleGuide.ComponentSeparator(${layout.styleGuide.componentSeparator})` }}
            >
                <Grid id='event-based-calendar-grid' items={gridItems} width={hardWidth} />
                <View style={{ margin: layout.styleGuide.spacer1 }}>
                    <View style={{ alignItems: 'center', justifyContent: 'center' }}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                            <H5 text={`Showing ${Math.min(eventItemsMax, sortedEvents.length)}/${sortedEvents.length} Events`} />
                            {sortedEvents.length > eventItemsMax && (
                                <Button
                                    style={{ marginHorizontal: layout.styleGuide.indent }}
                                    onPress={() => setEventItemsMax(Math.min(eventItemsMax + eventItemsLoadMoreCount, sortedEvents.length))}
                                >
                                    Load More
                                </Button>
                            )}
                        </View>
                        {sortedEvents.length <= eventItemsMax && (
                            <Text style={{ margin: layout.styleGuide.indent }}>
                                Adjust the <Button onPress={() => setFilterMenuVisible(!filterMenuVisible)}>Filter</Button> to view more items
                            </Text>
                        )}
                    </View>
                </View>
            </View>
        );
    };

    const calenderLayouts = Object.values(CalendarLayoutStyle);

    return (
        <View dataSet={{ umbracoComponentName: nodeTypeAlias }} style={{ width }}>
            <FilterMenu
                applyFilters={appliedFilters => {
                    setEventItemsMax(eventItemsInitialCount);
                    setFilters(appliedFilters);
                }}
                filters={filters}
                filterState={[filterMenuVisible, setFilterMenuVisible]}
            />
            <DatePickerMenu
                handleDateChange={e => setSelectedDate(e)}
                selectedDateState={[selectedDate, setSelectedDate]}
                datePickerState={[datePickerVisible, setDatePickerVisible]}
            />
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', margin: layout.styleGuide.spacer1 }}>
                <H1 text='Calendar' />
                <View style={{ flexDirection: 'row' }}>
                    <Button
                        buttonStyle={styles.buttonStyle}
                        titleStyle={styles.buttonTitleStyle}
                        title={layout.device.isMobile ? undefined : calendarLayoutStyle === 1 ? 'Calendar View' : 'Grid View'}
                        icon={{
                            color: '#153d6e',
                            name: calendarLayoutStyle === 1 ? 'calendar' : 'th-large',
                            size: 16,
                            style: { marginRight: 2 },
                            type: 'font-awesome'
                        }}
                        onPress={toggleCalendarLayout}
                    />
                    <Button
                        buttonStyle={styles.buttonStyle}
                        containerStyle={{ marginLeft: layout.styleGuide.indent }}
                        icon={{
                            color: '#153d6e',
                            name: 'filter',
                            size: 12,
                            style: { marginRight: 2 },
                            type: 'font-awesome-5'
                        }}
                        onPress={() => setFilterMenuVisible(!filterMenuVisible)}
                        title={layout.device.isMobile ? undefined : 'Filter'}
                        titleStyle={styles.buttonTitleStyle}
                        type='clear'
                    />
                </View>
            </View>
            {calenderLayouts[calendarLayoutStyle] === CalendarLayoutStyle.calendarBased && calendarBased()}
            {calenderLayouts[calendarLayoutStyle] === CalendarLayoutStyle.eventBased && eventBased()}
        </View>
    );
};

Calendar.propTypes = {
    events: PropTypes.arrayOf(PropTypes.object),
    subTitle: PropTypes.string,
    title: PropTypes.string
};

export default EventCalendar;

export const CalendarLayoutStyle = Object.freeze({
    calendarBased: 'CalendarLayoutStyle.calendarBased',
    eventBased: 'CalendarLayoutStyle.eventBased'
});

export const styles = StyleSheet.create({
    buttonStyle: {
        backgroundColor: '#f7f8f9',
        borderColor: '#153d6e',
        borderWidth: StyleSheet.hairlineWidth
    },
    buttonTitleStyle: {
        color: '#153d6e',
        fontFamily: 'GothamBook',
        fontSize: Platform.OS.equals('web') ? '16pt' : 16,
        letterSpacing: 0.25,
        lineHeight: Platform.OS.equals('web') ? '21pt' : 21
    }
});
